import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  LoadingState,
  PageHeader,
  Table,
  Toast,
  IconArrowLeft,
} from "../ui";
import { applyOrderInput, mapGroupToRows, moveRow } from "./groupOrderUtils";

function toDraftMap(groups) {
  const draft = {};
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    draft[group.id] = mapGroupToRows(group);
  });
  return draft;
}

export default function TournamentGroupOrderPage() {
  const { tournamentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [info, setInfo] = useState("");
  const [tournamentName, setTournamentName] = useState("Torneo");
  const [canManage, setCanManage] = useState(false);
  const [groups, setGroups] = useState([]);
  const [draftByGroup, setDraftByGroup] = useState({});
  const [savingByGroup, setSavingByGroup] = useState({});

  const refreshData = useCallback(async () => {
    if (!tournamentId) return;
    setLoading(true);
    setLoadError("");
    try {
      const [tournamentRes, accessRes, groupsRes] = await Promise.all([
        axios.get(`${appConstants.API_URL}/api/tournaments/${tournamentId}`),
        axios.get(`${appConstants.API_URL}/api/tournaments/${tournamentId}/access`),
        axios.get(`${appConstants.API_URL}/api/tournamentgroups/${tournamentId}/teams/ordered`),
      ]);

      setTournamentName(
        tournamentRes.data?.name || tournamentRes.data?.Name || "Torneo"
      );
      setCanManage(Boolean(accessRes.data?.canManage));
      const loadedGroups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
      setGroups(loadedGroups);
      setDraftByGroup(toDraftMap(loadedGroups));
    } catch (error) {
      if (error?.response?.status === 403) {
        setLoadError(
          "No tienes permisos para cambiar el orden manual del grupo en este torneo."
        );
      } else if (error?.response?.status === 404) {
        setLoadError("No se encontro el torneo solicitado.");
      } else {
        setLoadError("No se pudo cargar la configuracion de desempates manuales.");
      }
      setGroups([]);
      setDraftByGroup({});
      setCanManage(false);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const hasGroups = groups.length > 0;

  const dirtyGroups = useMemo(() => {
    const dirty = new Set();
    groups.forEach((group) => {
      const current = mapGroupToRows(group);
      const draft = draftByGroup[group.id] ?? [];
      if (current.length !== draft.length) {
        dirty.add(group.id);
        return;
      }
      const changed = current.some((row, index) => {
        const d = draft[index];
        return !d || d.groupTeamId !== row.groupTeamId || d.order !== row.order;
      });
      if (changed) dirty.add(group.id);
    });
    return dirty;
  }, [groups, draftByGroup]);

  const updateGroupDraft = (groupId, updater) => {
    setDraftByGroup((prev) => {
      const currentRows = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      return {
        ...prev,
        [groupId]: updater(currentRows),
      };
    });
  };

  const handleOrderInput = (groupId, groupTeamId, value) => {
    updateGroupDraft(groupId, (rows) => applyOrderInput(rows, groupTeamId, value));
  };

  const handleMove = (groupId, groupTeamId, direction) => {
    updateGroupDraft(groupId, (rows) => moveRow(rows, groupTeamId, direction));
  };

  const handleResetGroup = (groupId) => {
    const sourceGroup = groups.find((g) => g.id === groupId);
    if (!sourceGroup) return;
    updateGroupDraft(groupId, () => mapGroupToRows(sourceGroup));
  };

  const handleSaveGroup = async (groupId) => {
    if (!canManage) return;
    const rows = draftByGroup[groupId] ?? [];
    if (!rows.length) return;

    setSaveError("");
    setInfo("");
    setSavingByGroup((prev) => ({ ...prev, [groupId]: true }));
    try {
      await axios.put(`${appConstants.API_URL}/api/tournamentgroups/teams/order`, [
        ...rows
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((row) => ({
            groupTeamId: row.groupTeamId,
            order: row.order,
          })),
      ]);
      setInfo("Orden manual guardado. Se aplicara cuando haya empate total.");
      await refreshData();
    } catch (error) {
      if (error?.response?.status === 403) {
        setSaveError("No tienes permisos para guardar este cambio.");
      } else {
        setSaveError("No se pudo guardar el orden manual del grupo.");
      }
    } finally {
      setSavingByGroup((prev) => ({ ...prev, [groupId]: false }));
    }
  };

  if (loading) {
    return <LoadingState label="Cargando desempates manuales..." className="min-h-[45vh]" />;
  }

  return (
    <div className="ui-page-wrap py-4 sm:py-6">
      <PageHeader
        title={`Desempate Manual - ${tournamentName}`}
        subtitle="Si dos o mas equipos empatan en todo, define el orden por sorteo oficial aqui."
        actions={
          <Button as={RouterLink} to={`/tournamentactions/${tournamentId}`} variant="outline">
            <IconArrowLeft size={16} />
            Volver al torneo
          </Button>
        }
      />

      {loadError ? (
        <Toast kind="error" className="mt-6">
          {loadError}
        </Toast>
      ) : null}
      {saveError ? (
        <Toast kind="warning" className="mt-4">
          {saveError}
        </Toast>
      ) : null}
      {info ? (
        <Toast kind="success" className="mt-4">
          {info}
        </Toast>
      ) : null}
      {!canManage && !loadError ? (
        <Toast kind="warning" className="mt-4">
          Solo un gestor con permisos de administracion puede guardar el orden manual.
        </Toast>
      ) : null}

      {!hasGroups && !loadError ? (
        <div className="mt-8">
          <EmptyState
            title="No hay grupos para ordenar"
            description="Crea grupos y asigna equipos antes de usar desempate manual."
            action={
              <Button as={RouterLink} to={`/tournamentactions/${tournamentId}/setup`} variant="gold">
                Ir a configuracion
              </Button>
            }
          />
        </div>
      ) : null}

      {hasGroups ? (
        <div className="mt-8 grid gap-5 xl:grid-cols-2">
          {groups.map((group) => {
            const rows = (draftByGroup[group.id] ?? []).slice().sort((a, b) => a.order - b.order);
            const isSaving = Boolean(savingByGroup[group.id]);
            const isDirty = dirtyGroups.has(group.id);
            return (
              <Card key={group.id}>
                <CardHeader>
                  <h2 className="font-display text-xl font-extrabold text-ink">{group.name}</h2>
                  <p className="mt-1 text-sm text-ink-muted">
                    Ajusta el orden final para el escenario de empate total.
                  </p>
                </CardHeader>
                <CardBody className="space-y-3">
                  <Table>
                    <thead>
                      <tr className="border-b border-arena/10 bg-cream/60 text-left text-xs uppercase tracking-wide text-ink-soft">
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">Equipo</th>
                        <th className="px-3 py-2">Pts</th>
                        <th className="px-3 py-2">GF</th>
                        <th className="px-3 py-2">GC</th>
                        <th className="px-3 py-2">PJ</th>
                        <th className="px-3 py-2">Orden</th>
                        <th className="px-3 py-2">Mover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={row.groupTeamId} className="border-b border-arena/8 text-sm last:border-b-0">
                          <td className="px-3 py-2 text-ink-soft">{index + 1}</td>
                          <td className="px-3 py-2 font-semibold text-ink">{row.teamName}</td>
                          <td className="px-3 py-2 text-ink-soft">{row.points}</td>
                          <td className="px-3 py-2 text-ink-soft">{row.goalsFor}</td>
                          <td className="px-3 py-2 text-ink-soft">{row.goalsAgainst}</td>
                          <td className="px-3 py-2 text-ink-soft">{row.played}</td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min={1}
                              max={rows.length}
                              value={row.order}
                              onChange={(event) =>
                                handleOrderInput(group.id, row.groupTeamId, event.target.value)
                              }
                              disabled={!canManage || isSaving}
                              className="w-20 rounded-lg border border-arena/20 bg-white px-2 py-1 text-sm text-ink focus-ring"
                              aria-label={`Orden para ${row.teamName}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleMove(group.id, row.groupTeamId, "up")}
                                disabled={!canManage || isSaving || index === 0}
                                className="focus-ring rounded-lg border border-arena/20 px-2 py-1 text-xs font-bold text-ink-soft disabled:opacity-40"
                                aria-label={`Subir ${row.teamName}`}
                              >
                                ^
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMove(group.id, row.groupTeamId, "down")}
                                disabled={!canManage || isSaving || index === rows.length - 1}
                                className="focus-ring rounded-lg border border-arena/20 px-2 py-1 text-xs font-bold text-ink-soft disabled:opacity-40"
                                aria-label={`Bajar ${row.teamName}`}
                              >
                                v
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
                <CardFooter className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="gold"
                    onClick={() => handleSaveGroup(group.id)}
                    disabled={!canManage || isSaving || !isDirty}
                  >
                    {isSaving ? "Guardando..." : "Guardar orden del grupo"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleResetGroup(group.id)}
                    disabled={isSaving || !isDirty}
                  >
                    Restaurar
                  </Button>
                  <span className="text-xs text-ink-muted">
                    Se usa como criterio final cuando los equipos siguen empatados.
                  </span>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
